import { useState, useEffect } from 'react';
import api from '../services/api';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentsModal({ title, subtitle, listUrl, downloadUrl, onClose }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(listUrl);
        if (!cancelled) setFiles(data);
      } catch {
        if (!cancelled) setError('Failed to load attachments from Site Tracker');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listUrl]);

  async function handleDownload(file) {
    setDownloadingId(file.id);
    try {
      const res = await api.get(downloadUrl(file.id), {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.extension ? `${file.title}.${file.extension}` : file.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-xl border border-navy-500 p-6" style={{ backgroundColor: '#142236' }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-white font-bold text-lg">Attachments</h2>
            <p className="text-gray-400 text-xs mt-0.5 font-mono">{title}{subtitle ? ` — ${subtitle}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition text-xl leading-none">&times;</button>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm py-6 text-center">Loading from Site Tracker…</p>
        ) : error ? (
          <p className="text-red-400 text-sm py-6 text-center">{error}</p>
        ) : files.length === 0 ? (
          <p className="text-gray-500 text-sm py-6 text-center">No files attached to this job in Site Tracker.</p>
        ) : (
          <ul className="space-y-2 max-h-96 overflow-y-auto">
            {files.map((f) => (
              <li key={f.id} className="flex items-center justify-between bg-navy-700 rounded px-3 py-2" style={{ backgroundColor: '#0f1a29' }}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs font-bold uppercase text-gold-500 bg-navy-600 px-2 py-0.5 rounded shrink-0" style={{ color: '#c9a84c' }}>
                    {f.extension || 'file'}
                  </span>
                  <span className="text-white text-sm truncate">{f.title}</span>
                  <span className="text-gray-500 text-xs shrink-0">{formatSize(f.size)}</span>
                </div>
                <button
                  onClick={() => handleDownload(f)}
                  disabled={downloadingId === f.id}
                  className="text-xs px-3 py-1 rounded font-bold uppercase tracking-wide transition shrink-0 ml-3 disabled:opacity-50"
                  style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
                >
                  {downloadingId === f.id ? '…' : 'Download'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
